import { create } from 'zustand';
import { readJson, removeStorage, writeJson } from '@/lib/storage';

export const WISHLIST_STORAGE_KEY = 'sbg.wishlist';
/** The server keeps at most 200 saved items per customer; the guest list mirrors that. */
export const WISHLIST_LIMIT = 200;

/**
 * A product id (UUID, RFC 9562 versions 1–8). Deliberately a regular expression, not the shared Zod
 * schema: this module is part of the entry bundle, and importing `@sbg/shared/schemas` here would pull
 * Zod and every schema into the first load (D-090).
 */
const PRODUCT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Anything in storage that is not a clean list of product ids is ignored — never trusted. */
export function parseStoredIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids: string[] = [];
  for (const item of value as unknown[]) {
    if (typeof item === 'string' && PRODUCT_ID.test(item) && !ids.includes(item)) ids.push(item);
    if (ids.length >= WISHLIST_LIMIT) break;
  }
  return ids;
}

function persist(ids: readonly string[]): void {
  if (ids.length > 0) writeJson(WISHLIST_STORAGE_KEY, ids);
  else removeStorage(WISHLIST_STORAGE_KEY);
}

interface GuestWishlistState {
  /** Newest first. */
  ids: readonly string[];
  hydrated: boolean;
  /** Save (`true`) or remove (`false`) one product; saving an already-saved product keeps its place. */
  setSaved: (productId: string, saved: boolean) => void;
  /** Drop specific ids (after they were merged into the account). */
  remove: (productIds: readonly string[]) => void;
  hydrate: () => void;
}

/**
 * The wishlist of a visitor who is not signed in: product ids kept on this device (DECISIONS D-101).
 * It is functional storage the visitor asked for by pressing a heart — the same category as the
 * language choice — and it is merged into the account when they sign in (`useWishlistSync`).
 * Read after the first client render, like the other persisted stores, so SSR and hydration agree.
 */
export const useGuestWishlist = create<GuestWishlistState>()((set, get) => ({
  ids: [],
  hydrated: false,

  setSaved: (productId, saved) => {
    const current = get().ids;
    if (current.includes(productId) === saved) return;
    const next = saved
      ? [productId, ...current].slice(0, WISHLIST_LIMIT)
      : current.filter((id) => id !== productId);
    set({ ids: next });
    persist(next);
  },

  remove: (productIds) => {
    const drop = new Set(productIds);
    const next = get().ids.filter((id) => !drop.has(id));
    set({ ids: next });
    persist(next);
  },

  hydrate: () => {
    set({ ids: parseStoredIds(readJson(WISHLIST_STORAGE_KEY)), hydrated: true });
  },
}));
